import { type PartialPropsOf } from '@blaze-runtime/engine-domain';

import {
  type ElementLabelWidthProps,
  getClearElementContentOverridesPropsPatchWithSimpleAlign,
  getLabelWidthOverride,
  shouldPatchProp,
} from '../../utils/elements/props-builder.utils';
import { PropsBuilder } from '../__base/props-builder';
import {
  type FormChildrenContentOverrides,
  type FormChildrenStyleOverrides,
} from '../form-container/form-container.model';

export class RadioPropsBuilder extends PropsBuilder<'radio'> {
  override setMargin = (margin: string | undefined): this => {
    this.patchProps({ style: { general: { offset: { margin } } } });
    return this;
  };

  override setParentContentProps = (
    elementProps: PartialPropsOf<'radio'>,
    overrides: FormChildrenContentOverrides | null,
    initialValues?: PartialPropsOf<'radio'>,
  ): this => {
    if (overrides === null) {
      this.patchProps(getClearElementContentOverridesPropsPatchWithSimpleAlign());
      return this;
    }

    if (
      shouldPatchProp(
        overrides.label?.position,
        'content.title.position',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ content: { title: { position: overrides.label.position } } });
    }

    if (
      shouldPatchProp(overrides.label?.align, 'content.title.align', elementProps, initialValues)
    ) {
      this.patchProps({ content: { title: { align: overrides.label.align } } });
    }

    const titlePatch = getLabelWidthOverride(overrides, elementProps as ElementLabelWidthProps);
    if (titlePatch) {
      this.patchProps(titlePatch);
    }

    return this;
  };

  override setParentStyleProps = (
    elementProps: PartialPropsOf<'radio'>,
    overrides: FormChildrenStyleOverrides | null,
    initialValues?: PartialPropsOf<'radio'>,
  ): this => {
    if (overrides === null) {
      this.patchProps({
        style: {
          general: { title: undefined, accent: undefined },
          radio: undefined,
        },
      });
      return this;
    }

    const { titleLabel, fieldLabel, fieldBox } = overrides;

    if (shouldPatchProp(titleLabel?.color, 'style.general.title', elementProps, initialValues)) {
      this.patchProps({ style: { general: { title: titleLabel.color } } });
    }

    if (shouldPatchProp(fieldLabel?.color, 'style.radio.label', elementProps, initialValues)) {
      this.patchProps({ style: { radio: { label: fieldLabel.color } } });
    }

    if (shouldPatchProp(fieldBox?.accent, 'style.general.accent', elementProps, initialValues)) {
      this.patchProps({ style: { general: { accent: fieldBox.accent } } });
    }
    if (shouldPatchProp(fieldBox?.fill, 'style.radio.fill', elementProps, initialValues)) {
      this.patchProps({ style: { radio: { fill: fieldBox.fill } } });
    }
    if (shouldPatchProp(fieldBox?.outline, 'style.radio.border', elementProps, initialValues)) {
      this.patchProps({ style: { radio: { border: fieldBox.outline } } });
    }

    return this;
  };

  constructor(props: PartialPropsOf<'radio'> | undefined) {
    super(props, { hasData: true, hasLabel: true });
  }
}
